import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { packageService } from '@/api/services/packageService'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { useDebounce, useSessionState, useScrollRestoration } from '@/shared/hooks'
import { cn } from '@/shared/utils'
import { useAuthStore } from '@/store/useAuthStore'
import { toast } from '@/store/useToastStore'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { PackageBulkEditModal } from './PackageBulkEditModal'
import {
  Search, Download, X, Plus, Edit2, Eye,
  ChevronLeft, ChevronRight, Infinity as InfinityIcon,
  Layers, Car, CheckSquare, Trash2,
  MapPin, Building2, Percent, GraduationCap, CalendarDays, BadgeCheck, Boxes, Wallet,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Can } from '@/shared/ui/Can'

// ── Helpers ───────────────────────────────────────────────────────────────────
function getPkgName(pkg: any, locale = 'en') {
  return pkg?.package_translations?.find((tr: any) =>
    tr?.locale?.toLowerCase() === locale.toLowerCase()
  )?.name ?? pkg?.package_translations?.[0]?.name ?? pkg?.name ?? '—'
}

function fmtDate(v?: string | null) {
  if (!v) return '—'
  try { return new Date(v).toLocaleDateString('en-GB').replace(/\//g, '-') } catch { return v }
}

// ── Color config by background_color ─────────────────────────────────────────
// Header/border tint per package tier. The price is NOT tinted — it renders in a
// white chip so it stays legible on every header colour.
const COLOR_MAP: Record<string, { header: string; border: string }> = {
  unlimited: { header: 'bg-purple-600',  border: 'border-purple-200' },
  gold:      { header: 'bg-brand-500',   border: 'border-brand-200'  },
  silver:    { header: 'bg-gray-400',    border: 'border-gray-200'   },
  bronze:    { header: 'bg-brand-700',   border: 'border-brand-200'  },
  trial:     { header: 'bg-gray-700',    border: 'border-gray-200'   },
  express:   { header: 'bg-red-600',     border: 'border-red-200'    },
  red:       { header: 'bg-red-600',     border: 'border-red-200'    },
  default:   { header: 'bg-brand-500',   border: 'border-brand-200'  },
}

// ── Package card ──────────────────────────────────────────────────────────────
// One figure in the card's stat strip (sessions / schools / commission).
function CardStat({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 px-2 py-3 border-e border-gray-100 last:border-e-0">
      <div className="flex items-center justify-center text-sm font-black text-gray-900 leading-none">{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 text-center leading-tight">{label}</div>
    </div>
  )
}

// A labelled attribute row (city / school / vehicle / status).
function CardInfo({ icon: Icon, label, children }: { icon: LucideIcon; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <div className="w-8 h-8 rounded-lg bg-gray-50 ring-1 ring-gray-100 flex items-center justify-center flex-shrink-0">
        <Icon size={14} className="text-gray-400" strokeWidth={2}/>
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 leading-tight">{label}</div>
        <div className="text-xs font-bold text-gray-800 truncate leading-tight mt-0.5">{children}</div>
      </div>
    </div>
  )
}

function PackageCard({ pkg, onEdit, onView, onDelete, selectable, selected, onToggleSelect, canEdit = true }: {
  pkg: any; onEdit: () => void; onView: () => void; onDelete?: () => void
  selectable?: boolean; selected?: boolean; onToggleSelect?: () => void; canEdit?: boolean
}) {
  const { t } = useTranslation()
  const colorKey = pkg.background_color?.toLowerCase() ?? 'default'
  const col = COLOR_MAP[colorKey] ?? COLOR_MAP.default
  const name = getPkgName(pkg)
  const minPrice = Math.round(Number(pkg.min_price ?? 0))
  const maxPrice = Math.round(Number(pkg.max_price ?? 0))
  // Show a range only when min/max actually differ, otherwise a single figure.
  const priceDisplay = maxPrice > minPrice
    ? `${minPrice} – ${maxPrice}`
    : `${minPrice}.00`
  const sessions = pkg.is_unlimited ? 'unlimited' : (pkg.number_of_sessions ?? '—')
  const totalSchool = pkg.vendor_count ?? pkg.schools_count ?? 50
  const commission = pkg.drivys_commision ?? 0
  const isPercentage = pkg.is_drivys_commision_percentage
  const commissionDisplay = isPercentage ? `${commission}%` : `AED ${commission}`
  const isPublished = pkg.is_published === 1 || pkg.is_published === true

  const cityName = pkg.package_city?.[0]?.city?.city_translations?.find(
    (tr: any) => tr?.locale === 'En'
  )?.name ?? pkg.city_name ?? t('packageList.card.defaultCity')
  const schoolName = pkg.vendor?.vendor_translations?.find(
    (tr: any) => tr?.locale?.toLowerCase() === 'en'
  )?.name ?? pkg.school_name ?? t('packageList.card.defaultSchool')
  const vehicleType = pkg.category?.category_translations?.find(
    (tr: any) => tr?.locale?.toLowerCase() === 'en'
  )?.name ?? t('packageList.card.defaultVehicle')

  return (
    <div className={cn('group bg-white rounded-2xl border shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden relative flex flex-col',
      selectable && selected ? 'ring-2 ring-brand-500 border-brand-500' : col.border)}>
      {selectable && (
        <button onClick={onToggleSelect}
          aria-label={selected ? 'Deselect package' : 'Select package'}
          className={cn('absolute top-3 start-3 z-10 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors shadow-sm',
            selected ? 'bg-brand-500 border-brand-500 text-white' : 'bg-white/95 border-gray-300 hover:border-brand-400')}>
          {selected && <BadgeCheck size={12} strokeWidth={3}/>}
        </button>
      )}

      {/* Header — package identity + price */}
      <div className={cn('flex items-center justify-between gap-3 px-4 py-3.5', col.header)}>
        <div className={cn('flex items-center gap-2.5 min-w-0', selectable && 'ps-6')}>
          <div className="w-8 h-8 rounded-lg bg-white/20 ring-1 ring-white/25 flex items-center justify-center flex-shrink-0">
            <Boxes size={16} className="text-white" strokeWidth={2}/>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-white/70 leading-tight">{t('packageList.card.packageLabel')}</div>
            <div className="text-sm font-bold text-white truncate leading-tight mt-0.5">{name}</div>
          </div>
        </div>
        {/* Price — white chip keeps it legible on every header colour */}
        <div className="flex-shrink-0 flex items-baseline gap-1 rounded-lg bg-white px-2.5 py-1.5 shadow-sm ring-1 ring-black/5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">AED</span>
          <span className="text-sm font-black text-gray-900 whitespace-nowrap leading-none">{priceDisplay}</span>
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-3 border-b border-gray-100 bg-gray-50/40">
        <CardStat icon={CalendarDays} label={t('packageList.card.numberOfSession')}
          value={pkg.is_unlimited ? <InfinityIcon size={16} strokeWidth={2.5}/> : sessions}/>
        <CardStat icon={Building2} label={t('packageList.card.totalSchool')} value={totalSchool}/>
        <CardStat icon={isPercentage ? Percent : Wallet} label={t('packageList.card.drivysCommission')} value={commissionDisplay}/>
      </div>

      {/* Attributes */}
      <div className="grid grid-cols-2 gap-3 px-4 py-3.5 border-b border-gray-100">
        <CardInfo icon={MapPin}        label={t('packageList.card.city')}>{cityName}</CardInfo>
        <CardInfo icon={GraduationCap} label={t('packageList.card.school')}>{schoolName}</CardInfo>
      </div>
      <div className="grid grid-cols-2 gap-3 px-4 py-3.5 items-center">
        <CardInfo icon={Car} label={t('packageList.card.vehicle')}>{vehicleType}</CardInfo>
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ring-1',
            isPublished ? 'bg-green-50 ring-green-100' : 'bg-gray-50 ring-gray-100')}>
            <BadgeCheck size={14} strokeWidth={2} className={isPublished ? 'text-green-600' : 'text-gray-400'}/>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 leading-tight">{t('packageList.card.status')}</div>
            <span className={cn('inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ring-1',
              isPublished ? 'bg-green-50 text-green-700 ring-green-200' : 'bg-gray-100 text-gray-500 ring-gray-200')}>
              {isPublished ? t('packageList.published') : t('packageList.unpublished')}
            </span>
          </div>
        </div>
      </div>

      {/* Actions — primary View, compact icon buttons for edit/delete */}
      <div className="mt-auto px-4 pb-4 pt-1 flex items-center gap-2">
        <button onClick={onView}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-gray-900 text-white text-xs font-bold hover:bg-gray-800 transition-colors">
          <Eye size={13} strokeWidth={2.5}/> {t('packageList.card.viewDetails')}
        </button>
        {canEdit && (
          <button onClick={onEdit} title={t('packageList.card.editPackage')} aria-label={t('packageList.card.editPackage')}
            className="w-10 h-10 flex items-center justify-center rounded-xl border border-brand-200 bg-brand-50 text-brand-600 hover:bg-brand-100 hover:border-brand-300 transition-colors flex-shrink-0">
            <Edit2 size={14} strokeWidth={2.5}/>
          </button>
        )}
        {canEdit && onDelete && (
          <Can perm="DELETE /admin/package/*">
          <button onClick={onDelete} title={t('packageList.card.deletePackage')} aria-label={t('packageList.card.deletePackage')}
            className="w-10 h-10 flex items-center justify-center rounded-xl border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 hover:border-red-300 transition-colors flex-shrink-0">
            <Trash2 size={14} strokeWidth={2.5}/>
          </button>
          </Can>
        )}
      </div>
    </div>
  )
}

// ── Skeleton card ─────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 animate-pulse overflow-hidden">
      <div className="h-14 bg-gray-200"/>
      <div className="grid grid-cols-3 gap-0 border-b border-gray-100">
        {[1,2,3].map(i=><div key={i} className="p-3 border-r border-gray-100 last:border-0"><div className="h-4 bg-gray-100 rounded mx-auto w-8"/><div className="h-2 bg-gray-100 rounded mt-1 w-16 mx-auto"/></div>)}
      </div>
      <div className="p-4 space-y-2">
        <div className="h-3 bg-gray-100 rounded w-3/4"/>
        <div className="h-3 bg-gray-100 rounded w-1/2"/>
      </div>
      <div className="px-4 pb-4 flex items-center gap-2">
        <div className="h-10 bg-gray-100 rounded-xl flex-1"/>
        <div className="h-10 w-10 bg-gray-100 rounded-xl flex-shrink-0"/>
        <div className="h-10 w-10 bg-gray-100 rounded-xl flex-shrink-0"/>
      </div>
    </div>
  )
}

// ── Paginator ─────────────────────────────────────────────────────────────────
function Paginator({ page, lastPage, total, perPage, onPage, onPerPage }: {
  page: number; lastPage: number; total: number; perPage: number
  onPage:(p:number)=>void; onPerPage:(n:number)=>void
}) {
  const { t } = useTranslation()
  const nums=(()=>{let s=Math.max(1,page-2);if(s+4>lastPage)s=Math.max(1,lastPage-4);return Array.from({length:Math.min(5,lastPage)},(_,i)=>s+i).filter(p=>p<=lastPage)})()
  return (
    <div className="bg-white rounded-2xl border border-gray-200 px-5 py-3 flex items-center justify-between gap-4 flex-wrap shadow-sm">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span>{t('packageList.paginator.itemsPerPage')}</span>
        <select value={perPage} onChange={e=>onPerPage(Number(e.target.value))}
          className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none">
          {[10,15,20,25,50].map(n=><option key={n} value={n}>{n}</option>)}
        </select>
        <span>{t('packageList.paginator.itemsRange', { from:(page-1)*perPage+1, to:Math.min(page*perPage,total), total })}</span>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={()=>onPage(Math.max(1,page-1))} disabled={page<=1} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40"><ChevronLeft size={14}/></button>
        {nums.map(p=><button key={p} onClick={()=>onPage(p)} className={cn('w-8 h-8 rounded-lg border text-xs font-semibold',p===page?'bg-brand-500 text-white border-brand-500':'border-gray-200 text-gray-600 hover:bg-gray-50')}>{p}</button>)}
        <button onClick={()=>onPage(Math.min(lastPage,page+1))} disabled={page>=lastPage} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40"><ChevronRight size={14}/></button>
        <span className="ms-1 text-xs text-gray-400">{t('packageList.paginator.of')} {lastPage}</span>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Main page
// ══════════════════════════════════════════════════════════════════════════════
export function PackageListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const currentUser   = useAuthStore((s) => s.user)
  const isSchoolAdmin = String((currentUser as any)?.user_type ?? '').toUpperCase() === 'SCHOOL_ADMIN'
  const mySchoolId    = (currentUser as any)?.school_id
  const [search,   setSearch]  = useState('')
  const [page,     setPage]    = useSessionState('package-list:page', 1)
  const [perPage,  setPerPage] = useSessionState('package-list:perPage', 15)
  const [filters, setFilters] = useState<Record<string, any>>({})
  // bulk-edit selection
  const [selectMode, setSelectMode]   = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [bulkOpen, setBulkOpen]       = useState(false)
  // delete (single + bulk) — mirrors the old dashboard's handleDeleteRow / handleDeleteRows
  const [deleteTarget,   setDeleteTarget]   = useState<any>(null)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [deleting,       setDeleting]       = useState(false)

  const toggleSelect = (id: number) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const exitSelect = () => { setSelectMode(false); setSelectedIds([]) }

  const handleDeleteSingle = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await packageService.delete(deleteTarget.id)
      toast.success(t('packageList.deleteSuccess'))
      qc.invalidateQueries({ queryKey: ['packages'] })
      setDeleteTarget(null)
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('packageList.deleteFailed'))
    } finally { setDeleting(false) }
  }

  const handleDeleteBulk = async () => {
    if (selectedIds.length === 0) return
    setDeleting(true)
    try {
      const results = await Promise.allSettled(selectedIds.map(id => packageService.delete(id)))
      const failed = results.filter(r => r.status === 'rejected').length
      if (failed > 0) toast.error(t('packageList.bulkDeletePartial', { failed, total: selectedIds.length }))
      else toast.success(t('packageList.bulkDeleteSuccess', { count: selectedIds.length }))
      qc.invalidateQueries({ queryKey: ['packages'] })
      setBulkDeleteOpen(false)
      exitSelect()
    } catch {
      toast.error(t('packageList.deleteFailed'))
    } finally { setDeleting(false) }
  }

  const debouncedSearch = useDebounce(search)

  const { data, isLoading } = useQuery({
    queryKey: ['packages', 'list', isSchoolAdmin, mySchoolId, { page, perPage, search: debouncedSearch, ...filters }],
    queryFn: () => isSchoolAdmin
      // School admins see only their own school's packages — search is the only filter.
      ? restClient.get(
          `${endpoints.school.package.getPackageBySchool}?locale=en&page=${page}&limit=${perPage}` +
          (mySchoolId ? `&school_id=${mySchoolId}` : '') +
          (debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : '')
        ).then(r => r.data)
      : packageService.list({
          page, limit: perPage,
          search: debouncedSearch || undefined,
          ...(filters.is_published !== undefined && filters.is_published !== '' ? { is_published: filters.is_published } : {}),
          ...(filters.number_of_sessions ? { number_of_sessions: filters.number_of_sessions } : {}),
          ...(filters.vendor_id ? { vendor_id: filters.vendor_id } : {}),
        }),
    staleTime: 30_000,
  })

  // Schools for filter
  const { data: schoolsData } = useQuery({
    queryKey: ['schools-for-pkg-filter'],
    queryFn: () => restClient.get(`${endpoints.school.list}?page=1&limit=1000`).then(r=>r.data),
    staleTime: Infinity,
  })
  const schools: any[] = schoolsData?.data ?? []

  useScrollRestoration('package-list', !isLoading)

  const rows: any[] = (data as any)?.data ?? []
  const total       = (data as any)?.meta?.total ?? (data as any)?.total ?? rows.length
  const lastPage    = Math.ceil(total / perPage) || 1

  const activeFilterCount = Object.values(filters).filter(Boolean).length

  const handleFilterChange = useCallback((k: string, v: any) => {
    setFilters(prev => ({ ...prev, [k]: v }))
    setPage(1)
  }, [])

  const handleFilterReset = () => { setFilters({}); setPage(1) }

  const handleExport = () => {
    const csv = [['ID','Name','Sessions','Price','Published'],
      ...rows.map((r:any)=>[r.id, getPkgName(r), r.number_of_sessions, r.min_price, r.is_published])
    ].map(r=>r.join(',')).join('\n')
    const a=document.createElement('a'); a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'})); a.download='packages.csv'; a.click()
  }

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
            <span>{t('packageList.breadcrumb.dashboard')}</span><span>/</span>
            <span className="text-brand-500 font-semibold">{t('packageList.breadcrumb.packageManagement')}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">{t('packageList.title')}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isSchoolAdmin && (
            <button onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs font-semibold transition-colors">
              <Download size={12}/> {t('packageList.download')}
            </button>
          )}

          {/* Bulk edit + Create — admins only */}
          {!isSchoolAdmin && (!selectMode ? (
            <Can perm="POST /admin/package/*">
            <button onClick={()=>setSelectMode(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-brand-300 text-brand-600 text-xs font-bold hover:bg-brand-50 transition-colors">
              <CheckSquare size={12}/> {t('packageList.editMultiple')}
            </button>
            </Can>
          ) : (
            <>
              <button onClick={()=>setBulkOpen(true)} disabled={selectedIds.length === 0}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-xs font-bold transition-colors disabled:opacity-50">
                <Edit2 size={12}/> {t('packageList.editSelected', { count: selectedIds.length })}
              </button>
              <button onClick={()=>setBulkDeleteOpen(true)} disabled={selectedIds.length === 0}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-bold transition-colors disabled:opacity-50">
                <Trash2 size={12}/> {t('packageList.deleteSelected', { count: selectedIds.length })}
              </button>
              <button onClick={exitSelect}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-xs font-semibold hover:bg-gray-50 transition-colors">
                <X size={12}/> {t('packageList.cancel')}
              </button>
            </>
          ))}

          {!isSchoolAdmin && (
            <Can perm="POST /admin/package/*">
            <button onClick={()=>navigate('/dashboard/package/create')}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-xs font-bold transition-colors">
              <Plus size={13}/> {t('packageList.createNew')}
            </button>
            </Can>
          )}
        </div>
      </div>

      {/* ── Filters — inline, one place (no side drawer) ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-2 bg-white min-w-44">
          <Search size={12} className="text-gray-400 flex-shrink-0"/>
          <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} placeholder={t('packageList.searchPlaceholder')}
            className="text-xs text-gray-600 placeholder-gray-400 focus:outline-none flex-1 bg-transparent min-w-0"/>
          {search && <button onClick={()=>setSearch('')}><X size={11} className="text-gray-300 hover:text-gray-500"/></button>}
        </div>
        {!isSchoolAdmin && (
          <>
            <select value={filters.is_published ?? ''} onChange={e=>handleFilterChange('is_published', e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-600 bg-white min-w-28">
              <option value="">{t('packageList.statusPlaceholder')}</option>
              <option value="1">{t('packageList.published')}</option>
              <option value="0">{t('packageList.unpublished')}</option>
            </select>
            <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-2 bg-white focus-within:ring-2 focus-within:ring-brand-400">
              <span className="text-xs font-semibold text-gray-500 whitespace-nowrap flex-shrink-0">{t('packageList.filterDrawer.numberOfSessions')}</span>
              <input type="number" min={0} value={filters.number_of_sessions ?? ''}
                onChange={e=>handleFilterChange('number_of_sessions', e.target.value)}
                placeholder={t('packageList.filterDrawer.sessionsPlaceholder')}
                className="text-xs text-gray-700 focus:outline-none w-12 bg-transparent"/>
            </div>
            <select value={filters.vendor_id ?? ''} onChange={e=>handleFilterChange('vendor_id', e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-600 bg-white min-w-36">
              <option value="">{t('packageList.filterDrawer.allSchools')}</option>
              {schools.map((s:any)=>{
                const n=s.vendor_translations?.find((tr:any)=>tr.locale?.toLowerCase()==='en')?.name??s.vendor_translations?.[0]?.name??t('packageList.filterDrawer.unknownSchool')
                return <option key={s.id} value={s.id}>{n}</option>
              })}
            </select>
            {activeFilterCount > 0 && (
              <button onClick={handleFilterReset}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold text-red-500 hover:bg-red-50 transition-colors">
                <X size={12}/> {t('packageList.filterDrawer.resetFilters')}
              </button>
            )}
          </>
        )}
      </div>

      {/* ── Card grid ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {isLoading
          ? Array.from({length:6}).map((_,i)=><SkeletonCard key={i}/>)
          : rows.length === 0
            ? (
              <div className="col-span-3 flex flex-col items-center justify-center py-20 text-center">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <Layers size={24} className="text-gray-300"/>
                </div>
                <p className="text-sm font-semibold text-gray-500">{t('packageList.noPackagesFound')}</p>
              </div>
            )
            : rows.map((pkg:any)=>(
              <PackageCard key={pkg.id} pkg={pkg}
                canEdit={!isSchoolAdmin}
                selectable={selectMode && !isSchoolAdmin}
                selected={selectedIds.includes(pkg.id)}
                onToggleSelect={()=>toggleSelect(pkg.id)}
                onEdit={()=>navigate(isSchoolAdmin ? `/school/package/${pkg.id}` : `/dashboard/package/${pkg.id}/edit`)}
                onDelete={()=>setDeleteTarget(pkg)}
                onView={()=>navigate(isSchoolAdmin ? `/school/package/${pkg.id}` : `/dashboard/package/${pkg.id}`)}/>
            ))
        }
      </div>

      {/* ── Pagination ── */}
      {!isLoading && total > 0 && (
        <Paginator page={page} lastPage={lastPage} total={total} perPage={perPage}
          onPage={setPage} onPerPage={n=>{setPerPage(n);setPage(1)}}/>
      )}

      {/* ── Bulk edit modal ── */}
      <PackageBulkEditModal
        open={bulkOpen}
        onClose={()=>setBulkOpen(false)}
        selectedIds={selectedIds}
        onSuccess={()=>{ qc.invalidateQueries({ queryKey: ['packages'] }); exitSelect() }}
      />

      {/* ── Delete confirmations ── */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={()=>setDeleteTarget(null)}
        onConfirm={handleDeleteSingle}
        loading={deleting}
        title={t('packageList.deleteTitle')}
        description={t('packageList.deleteDesc', { name: deleteTarget ? getPkgName(deleteTarget) : '' })}
        confirmLabel={t('packageList.card.deletePackage')}
      />
      <ConfirmDialog
        open={bulkDeleteOpen}
        onClose={()=>setBulkDeleteOpen(false)}
        onConfirm={handleDeleteBulk}
        loading={deleting}
        title={t('packageList.bulkDeleteTitle')}
        description={t('packageList.bulkDeleteDesc', { count: selectedIds.length })}
        confirmLabel={t('packageList.deleteSelected', { count: selectedIds.length })}
      />
    </div>
  )
}
